import React, { useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const FONT_WEIGHTS = [
  { value: 400, label: "Normal" },
  { value: 500, label: "Medium" },
  { value: 600, label: "Semibold" },
  { value: 700, label: "Bold" },
];

export function GroupColorPresetsManager({ authToken }) {
  const { toast } = useToast();
  const [colors, setColors] = useState([]);
  const [badgeSettings, setBadgeSettings] = useState({
    font_size: 14,
    font_weight: 600,
    text_color_mode: "auto",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` },
  });

  useEffect(() => {
    loadColors();
  }, [authToken]);

  const loadColors = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/group-color-presets`);
      setColors(res.data.colors || []);
      setBadgeSettings(res.data.badge_settings || {
        font_size: 14,
        font_weight: 600,
        text_color_mode: "auto",
      });
    } catch (e) {
      console.error("Fehler beim Laden der Gruppenfarben:", e);
    } finally {
      setLoading(false);
    }
  };

  const saveColors = async () => {
    const validColors = colors.filter((c) => c.hex && c.hex.trim());
    if (validColors.length === 0) {
      toast({ title: "Mindestens eine Farbe erforderlich", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API_BASE}/api/group-color-presets`,
        { colors: validColors, badge_settings: badgeSettings },
        authHeaders()
      );
      toast({ title: "Gruppenfarben gespeichert" });
      setColors(validColors);
    } catch (e) {
      console.error("Fehler beim Speichern:", e);
      toast({ title: "Fehler beim Speichern", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const addColor = () => {
    setColors([...colors, { hex: "#3b82f6", name: "", text_color: "#ffffff" }]);
  };

  const removeColor = (index) => {
    if (colors.length <= 1) {
      toast({ title: "Mindestens eine Farbe erforderlich", variant: "destructive" });
      return;
    }
    setColors(colors.filter((_, i) => i !== index));
  };

  const updateColor = (index, field, value) => {
    const updated = [...colors];
    updated[index] = { ...updated[index], [field]: value };
    setColors(updated);
  };

  const moveColor = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= colors.length) return;
    const updated = [...colors];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setColors(updated);
  };

  // Automatische Textfarbe berechnen
  const getAutoTextColor = (hex) => {
    if (!hex) return "#ffffff";
    const c = hex.replace("#", "");
    const r = parseInt(c.substr(0, 2), 16);
    const g = parseInt(c.substr(2, 2), 16);
    const b = parseInt(c.substr(4, 2), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? "#1f2937" : "#ffffff";
  };

  // Effektive Textfarbe für Vorschau
  const getEffectiveTextColor = (colorObj) => {
    if (badgeSettings.text_color_mode === "manual" && colorObj.text_color) {
      return colorObj.text_color;
    }
    return getAutoTextColor(colorObj.hex);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Badge-Einstellungen */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-medium text-slate-700 mb-4">Badge-Einstellungen (systemweit)</p>
        
        <div className="grid grid-cols-3 gap-4">
          {/* Schriftgröße */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Schriftgröße</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                min="10"
                max="24"
                value={badgeSettings.font_size}
                onChange={(e) => setBadgeSettings({ ...badgeSettings, font_size: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm font-mono w-12 text-right">{badgeSettings.font_size}px</span>
            </div>
          </div>

          {/* Schriftstärke */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Schriftstärke</label>
            <select
              value={badgeSettings.font_weight}
              onChange={(e) => setBadgeSettings({ ...badgeSettings, font_weight: parseInt(e.target.value) })}
              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-slate-400 focus:outline-none"
            >
              {FONT_WEIGHTS.map((fw) => (
                <option key={fw.value} value={fw.value}>{fw.label} ({fw.value})</option>
              ))}
            </select>
          </div>

          {/* Textfarben-Modus */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Textfarbe</label>
            <select
              value={badgeSettings.text_color_mode}
              onChange={(e) => setBadgeSettings({ ...badgeSettings, text_color_mode: e.target.value })}
              className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-slate-400 focus:outline-none"
            >
              <option value="auto">Automatisch (hell/dunkel)</option>
              <option value="manual">Manuell pro Farbe</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vorschau */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-medium text-slate-600 mb-3">Vorschau der Gruppen-Badges</p>
        <div className="flex flex-wrap gap-2 bg-white p-3 rounded-lg">
          {colors.map((color, index) => (
            <div
              key={index}
              className="rounded-full px-3 py-1.5"
              style={{
                backgroundColor: color.hex,
                color: getEffectiveTextColor(color),
                fontSize: `${badgeSettings.font_size}px`,
                fontWeight: badgeSettings.font_weight,
              }}
            >
              {color.name || `Gruppe ${index + 1}`} · 10:00
            </div>
          ))}
        </div>
      </div>

      {/* Farbliste */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-slate-700">Farben definieren</p>
          <button
            onClick={addColor}
            className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Farbe hinzufügen
          </button>
        </div>

        <div className="space-y-2">
          {colors.map((color, index) => (
            <div
              key={index}
              className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3"
            >
              {/* Reihenfolge-Buttons */}
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => moveColor(index, -1)}
                  disabled={index === 0}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  onClick={() => moveColor(index, 1)}
                  disabled={index === colors.length - 1}
                  className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Hintergrund-Farbwähler */}
              <div>
                <label className="block text-xs text-slate-500 mb-1">Hintergrund</label>
                <input
                  type="color"
                  value={color.hex}
                  onChange={(e) => updateColor(index, "hex", e.target.value)}
                  className="h-8 w-12 cursor-pointer rounded border-0"
                />
              </div>

              {/* HEX-Eingabe */}
              <div className="w-28">
                <label className="block text-xs text-slate-500 mb-1">HEX</label>
                <input
                  type="text"
                  value={color.hex}
                  onChange={(e) => updateColor(index, "hex", e.target.value)}
                  placeholder="#3b82f6"
                  className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-mono focus:border-slate-400 focus:outline-none"
                />
              </div>

              {/* Textfarbe (nur wenn manuell) */}
              {badgeSettings.text_color_mode === "manual" && (
                <>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Schrift</label>
                    <input
                      type="color"
                      value={color.text_color || "#ffffff"}
                      onChange={(e) => updateColor(index, "text_color", e.target.value)}
                      className="h-8 w-12 cursor-pointer rounded border-0"
                    />
                  </div>
                  <div className="w-24">
                    <label className="block text-xs text-slate-500 mb-1">Schrift-HEX</label>
                    <input
                      type="text"
                      value={color.text_color || "#ffffff"}
                      onChange={(e) => updateColor(index, "text_color", e.target.value)}
                      placeholder="#ffffff"
                      className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm font-mono focus:border-slate-400 focus:outline-none"
                    />
                  </div>
                </>
              )}

              {/* Name */}
              <div className="flex-1">
                <label className="block text-xs text-slate-500 mb-1">Name (optional)</label>
                <input
                  type="text"
                  value={color.name}
                  onChange={(e) => updateColor(index, "name", e.target.value)}
                  placeholder="z.B. Rot, Blau..."
                  className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-slate-400 focus:outline-none"
                />
              </div>

              {/* Löschen */}
              <button
                onClick={() => removeColor(index)}
                className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-500 self-end"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Speichern */}
      <div className="flex justify-end">
        <button
          onClick={saveColors}
          disabled={saving}
          className="rounded-full bg-slate-900 px-6 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {saving ? "Speichern..." : "Einstellungen speichern"}
        </button>
      </div>

      {/* Hinweis */}
      <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-700">
        <strong>Hinweis:</strong> Diese Einstellungen gelten systemweit für alle Gruppen-Badges in der gesamten Anwendung.
      </div>
    </div>
  );
}
